import React from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import _ from 'lodash';

import * as path from '../../../util/pathConstructor';
import * as eventsApi from '../../../api/eventsApi';
import tagular from '../../../util/tagular';
import { SIMPLE_SELECT } from '../../../util/answerOptionTypes';
import { getQueryParameters, serializeQueryParameters } from '../../../util/utils';
import { getAnswerAggregationStrategy } from '../../../strategies/answerAggregationStrategies/answerAggregationStrategyFactory';
import { getSortStrategy } from '../../../strategies/sortStrategies/sortStrategyFactory';
import { updateFilteredResults } from '../../../actions/filteredResultActions';
import { fetchResults } from '../../../actions/resultActions';
import { receiveAggregateFilters } from '../../../actions/selectedFilter/inResultsActions';

import './nav.scss';
import arrowLeft from '../../../assets/arrow-left.svg';
import arrowRight from '../../../assets/arrow-right.svg';

class Nav extends React.Component {
    constructor(props) {
        super(props);

        this.enableResultsLinkout = this.enableResultsLinkout.bind(this);
        this.handleLinkoutClick = this.handleLinkoutClick.bind(this);
        this.handleLastPromptNext = this.handleLastPromptNext.bind(this);
    }

    componentDidUpdate(prevProps) {
        const { assessment, allSelectedFilters } = this.props;

        if (assessment !== prevProps.assessment) {
            if (this.resultsUnavailable()) {
                this.props.fetchResults(assessment.name);
            }
        }

        if (allSelectedFilters !== prevProps.allSelectedFilters && this.isLastPrompt()) {
            const aggregatedFilters = this.aggregateFilters();
            this.props.receiveAggregateFilters(aggregatedFilters);
            this.props.updateFilteredResults(aggregatedFilters);
        }
    }

    previousPath() {
        const {
            assessment: { name },
            location
        } = this.props;

        const { promptQueue } = this.props;
        const currPromptId = this.props.match.params.promptId;

        const currPromptPosition = promptQueue.indexOf(currPromptId);

        if (currPromptPosition === 0) {
            return '#';
        }

        const previousPrompt = promptQueue[currPromptPosition - 1];

        return path.getPromptPath(name, location.search, previousPrompt);
    }

    nextPath() {
        if (this.answerCount() < this.props.prompt.minSelection) {
            return '#';
        }

        const {
            assessment: { name },
            location
        } = this.props;

        const nextPrompt = this.getNextPrompt();

        if (!nextPrompt) {
            return path.getLoadingPath(name, location.search);
        }

        return path.getPromptPath(name, location.search, nextPrompt);
    }

    getNextPrompt() {
        const { promptQueue } = this.props;
        const currPromptId = this.props.match.params.promptId;

        const currPromptPosition = promptQueue.indexOf(currPromptId);
        return promptQueue[currPromptPosition + 1];
    }

    resultsUnavailable() {
        return !this.props.allResults || this.props.allResults.length < 1;
    }

    isLastPrompt() {
        const nextPrompt = this.getNextPrompt();
        return !nextPrompt;
    }

    outboundResultsPath() {
        const { assessment, location, inline } = this.props;
        const qs = getQueryParameters(location.search);

        const trackingData = {};
        if (inline.parentMetadata && inline.parentMetadata.ads && inline.parentMetadata.ads.k1) {
            trackingData.k1 = inline.parentMetadata.ads.k1;
        }

        if (inline.parentMetadata && inline.parentMetadata.ads && inline.parentMetadata.ads.k2) {
            trackingData.k2 = inline.parentMetadata.ads.k2;
        }

        if (
            inline.parentMetadata &&
            inline.parentMetadata.ads &&
            inline.parentMetadata.ads.msiteID
        ) {
            trackingData.msiteId = inline.parentMetadata.ads.msiteID;
        }
        qs.td = btoa(JSON.stringify(trackingData));

        const aggregatedFilters = this.aggregateFilters();

        if (aggregatedFilters.length > 0) {
            const filters = aggregatedFilters.join(',');
            qs.q = filters;
        }

        const resultsPath = path.getResultsPath(
            assessment.name,
            `?${serializeQueryParameters(qs)}`,
            { clearSearchParams: ['in'] }
        );
        return resultsPath;
    }

    aggregateFilters() {
        const { assessment, allSelectedFilters, prompts } = this.props;
        const aggregationStrategy = getAnswerAggregationStrategy(assessment.type);
        const aggregated = aggregationStrategy(assessment, allSelectedFilters, prompts);

        return aggregated;
    }

    toggleNextEnable() {
        if (this.answerCount() >= this.props.prompt.minSelection) {
            return 'enabled';
        }
        return 'disabled';
    }

    toggleBackEnable() {
        const currPromptId = this.props.match.params.promptId;
        const firstPromptId = this.props.promptQueue[0];

        if (currPromptId === firstPromptId) {
            return 'disabled';
        }
        return 'enabled';
    }

    answerCount() {
        const { selectedFilters } = this.props;
        let count = 0;

        for (const item of selectedFilters) {
            if (item !== null) {
                count++;
            }
        }

        return count;
    }

    enableResultsLinkout() {
        const { inline } = this.props;
        const isInline = inline.info.status;

        return isInline && this.isLastPrompt() && inline.config.linkoutResults;
    }

    handleLinkoutClick(e) {
        const { prompt, assessment, inline } = this.props;

        eventsApi.resultClicked(prompt.question, assessment, {
            text: e.target.innerText,
            inlineParentMetadata: inline.parentMetadata
        });

        this.handleLastPromptNext(e);

        this.sendSubmittedEvents(e);
    }

    handleLastPromptNext(e) {
        const { saveResultsConsent, filteredResults, selectedFiltersArray, assessment } =
            this.props;

        if (this.isLastPrompt() && assessment.resultsConfig.saveResult && saveResultsConsent) {
            const sort = getSortStrategy(assessment.resultsConfig);
            const sortedResults = sort(filteredResults, selectedFiltersArray);
            const options = { assessment, sortedResults };

            tagular.sendFormOutcomeReceivedEvent(e, options);

            console.log('Sending results to Cohesion:', sortedResults);
        }
    }

    sendSubmittedEvents(e) {
        const { allSelectedFilters, prompts, assessment, inline } = this.props;

        const selectedAnswerOptions = [];

        for (const promptId in allSelectedFilters) {
            const selectedFilters = allSelectedFilters[promptId];

            for (let i = 0; i < selectedFilters.length; i++) {
                const selectedFilter = selectedFilters[i];

                if (selectedFilter != null) {
                    const { question, filters } = prompts[promptId];
                    const answerCopy = filters.find(
                        filter => filter.tag == selectedFilter
                    ).answerDisplay;

                    const eventOptions = {
                        position: promptId,
                        inlineParentMetadata: inline.parentMetadata
                    };

                    eventsApi.resultsSubmitted(question, answerCopy, assessment, eventOptions);

                    selectedAnswerOptions.push({
                        question,
                        answer: answerCopy,
                        id: promptId,
                        answerType: SIMPLE_SELECT
                    });
                }
            }
        }

        tagular.sendFormSubmittedEvent(e, {
            assessment,
            formElements: selectedAnswerOptions
        });
    }

    render() {
        const { prompts, inline, site } = this.props;

        let nav;
        if (prompts) {
            nav = (
                <ul className="nav-container">
                    <Link
                        to={`${this.previousPath()}`}
                        className={`nav-button ${site} back`}
                        id={this.toggleBackEnable()}
                    >
                        <div className="button-content back">
                            <img className="left nav icon" src={arrowLeft} alt="" />
                            <span className="back">BACK</span>
                        </div>
                    </Link>

                    {this.enableResultsLinkout() ? (
                        <a
                            target="_blank"
                            onClick={this.handleLinkoutClick}
                            href={`/assessment${this.outboundResultsPath()}`}
                            className={`nav-button ${site} next`}
                            id={this.toggleNextEnable()}
                            rel="noreferrer"
                        >
                            <div className="button-content next">
                                <span className="next">
                                    {inline.config.linkoutCTA
                                        ? inline.config.linkoutCTA
                                        : 'SEE MY RESULTS'}
                                </span>
                                <img className="right nav icon" src={arrowRight} alt="" />
                            </div>
                        </a>
                    ) : (
                        <Link
                            to={`${this.nextPath()}`}
                            className={`nav-button ${site} next`}
                            id={this.toggleNextEnable()}
                            onClick={this.handleLastPromptNext}
                        >
                            <div className="button-content next">
                                <span className="next">NEXT</span>
                                <img className="right nav icon" src={arrowRight} alt="" />
                            </div>
                        </Link>
                    )}
                </ul>
            );
        }

        return <>{nav}</>;
    }
}

const mapStateToProps = (state, ownProps) => {
    const { promptId } = ownProps.match.params;
    const selectedFilters = state.session.selectedFilters.inAssessment[promptId] || [];

    return {
        ...ownProps,
        assessment: state.app.assessment,
        inline: state.inline,
        prompt: state.session.currentPrompt,
        promptQueue: state.session.promptQueue,
        selectedFilters,
        allSelectedFilters: state.session.selectedFilters.inAssessment,
        selectedFiltersArray: state.session.selectedFilters.inResults,
        prompts: state.entities.prompts,
        filteredResults: state.session.filteredResults,
        allResults: state.entities.results
    };
};

const mapDispatchToProps = dispatch => {
    return {
        updateFilteredResults: filters => dispatch(updateFilteredResults(filters)),
        fetchResults: indexName => dispatch(fetchResults(indexName)),
        receiveAggregateFilters: filters => dispatch(receiveAggregateFilters(filters))
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Nav));
